import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  TextField,
  MenuItem,
  Container,
  Tooltip,
  Paper,
  Stack,
  Chip,
  Button,
  FormControlLabel,
  Switch,
  InputAdornment,
  Avatar,
  CardHeader,
  CardActions,
  Fade,
  Zoom
} from '@mui/material';
import { 
  Edit, 
  Delete, 
  Visibility, 
  Share, 
  Add, 
  VisibilityOff, 
  Search, 
  Menu as MenuIcon, 
  Apps as AppsIcon,
  TrendingUp,
  BusinessCenter,
  Phone,
  Email,
  Language,
  QrCodeScanner,
  Analytics
} from '@mui/icons-material';
import CardShare from './CardShare';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { usePage } from '../../context/PageContext';
import { useSidebar } from '../../context/SidebarContext';
import { useTheme } from '@mui/material/styles';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 }
};

const CardList = ({ cards, onEdit, onDelete, onToggleVisibility }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCards, setFilteredCards] = useState(cards);
  const { setPageTitle } = usePage();
  const { toggleSidebar } = useSidebar();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    setPageTitle('My Cards');
  }, [setPageTitle]);

  useEffect(() => {
    setFilteredCards(
      cards.filter(card =>
        card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        card.companyName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, cards]);

  const handleShareClick = (card) => {
    setSelectedCard(card);
    setShareDialogOpen(true);
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setSelectedCard(null);
  };

  const DashboardHeader = () => (
    <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
      <Paper elevation={0} sx={{ 
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        p: 4,
        borderRadius: 3,
        mb: 3,
        position: 'relative',
        overflow: 'hidden',
        border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : 'none'
      }}>
        <Box sx={{ 
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: theme.palette.mode === 'dark' 
            ? 'rgba(144, 202, 249, 0.1)' 
            : 'rgba(255,255,255,0.1)',
        }} />
        <Box sx={{ 
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: theme.palette.mode === 'dark' 
            ? 'rgba(144, 202, 249, 0.05)' 
            : 'rgba(255,255,255,0.05)',
        }} />
        <Grid container alignItems="center" spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} md={8}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              Welcome Back! 👋
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
              Manage your digital business cards and track their performance
            </Typography>
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                icon={<BusinessCenter />} 
                label={`${cards.length} Active Card${cards.length !== 1 ? 's' : ''}`}
                sx={{ 
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(144, 202, 249, 0.2)' 
                    : 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  border: theme.palette.mode === 'dark' ? '1px solid rgba(144, 202, 249, 0.3)' : 'none'
                }}
              />
              <Chip 
                icon={<Visibility />} 
                label={`${cards.filter(c => c.is_published).length} Published`}
                sx={{ 
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(144, 202, 249, 0.2)' 
                    : 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  border: theme.palette.mode === 'dark' ? '1px solid rgba(144, 202, 249, 0.3)' : 'none'
                }}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
            <Avatar sx={{ 
              width: 80, 
              height: 80, 
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(144, 202, 249, 0.2)' 
                : 'rgba(255,255,255,0.2)',
              mx: 'auto',
              mb: 2,
              border: theme.palette.mode === 'dark' ? '2px solid rgba(144, 202, 249, 0.3)' : 'none'
            }}>
              <BusinessCenter sx={{ fontSize: 40 }} />
            </Avatar>
            <Button 
              variant="contained" 
              sx={{ 
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(144, 202, 249, 0.2)' 
                  : 'rgba(255,255,255,0.2)',
                '&:hover': { 
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(144, 202, 249, 0.3)' 
                    : 'rgba(255,255,255,0.3)' 
                },
                border: theme.palette.mode === 'dark' ? '1px solid rgba(144, 202, 249, 0.3)' : 'none',
                color: 'white'
              }}
              startIcon={<Analytics />}
              component={Link}
              to="/analytics"
            >
              View Analytics
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </motion.div>
  );

  const EmptyState = () => (
    <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <Paper elevation={0} sx={{ 
        textAlign: 'center', 
        p: 6, 
        mt: 4,
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
          : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        borderRadius: 3,
        border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : 'none'
      }}>
        <Avatar sx={{ 
          width: 80, 
          height: 80, 
          backgroundColor: 'primary.main',
          mx: 'auto',
          mb: 3,
          border: theme.palette.mode === 'dark' ? '2px solid rgba(144, 202, 249, 0.3)' : 'none'
        }}>
          <BusinessCenter sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h4" gutterBottom sx={{ 
          fontWeight: 'bold',
          color: theme.palette.mode === 'dark' ? 'white' : 'inherit'
        }}>
          No Digital Cards Yet
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3, fontSize: '1.1rem' }}>
          It looks like there are no cards assigned to you yet. Please contact an admin to get started.
        </Typography>
        <Button variant="contained" size="large" sx={{ 
          borderRadius: 3,
          ...(theme.palette.mode === 'dark' && {
            backgroundColor: 'primary.main',
            '&:hover': { backgroundColor: 'primary.dark' }
          })
        }}>
          Contact Support
        </Button>
      </Paper>
    </motion.div>
  );

  return (
    <Box sx={{ 
        minHeight: '100vh',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #1a1a2e 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #f5576c 75%, #4facfe 100%)',
        p: 3
    }}>
      {/* Dashboard Header */}
      {cards.length > 0 && <DashboardHeader />}
      
      {/* Search and Filters */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Paper elevation={0} sx={{ 
          p: 3, 
          borderRadius: 3, 
          mb: 3, 
          backgroundColor: theme.palette.mode === 'dark' 
            ? 'rgba(30,30,30,0.9)' 
            : 'rgba(255,255,255,0.9)',
          border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : 'none'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={toggleSidebar} sx={{ display: { lg: 'none' } }}>
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                My Digital Cards
              </Typography>
            </Stack>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                variant="outlined"
                placeholder="Search by name or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ 
                  minWidth: { xs: '100%', sm: '300px' },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.05)' 
                      : 'white',
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : 'none'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
        </Paper>
      </motion.div>

      {cards.length === 0 ? <EmptyState /> : (
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <Grid container spacing={3}>
            {filteredCards.map((card, index) => (
              <Grid item key={card.id} xs={12} sm={6} md={4} lg={3}>
                <motion.div 
                  variants={itemVariants} 
                  style={{ height: '100%' }}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                >
                  <Card 
                    elevation={0}
                    sx={{ 
                      height: '100%',
                      borderRadius: 3,
                      background: theme.palette.mode === 'dark' 
                        ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
                        : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                      border: theme.palette.mode === 'dark' 
                        ? '1px solid rgba(255,255,255,0.1)'
                        : '1px solid rgba(0,0,0,0.08)',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 8px 32px rgba(0,0,0,0.4)'
                        : '0 8px 32px rgba(0,0,0,0.08)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 20px 60px rgba(0,0,0,0.5)'
                          : '0 20px 60px rgba(0,0,0,0.12)',
                        transform: 'translateY(-8px)',
                        border: theme.palette.mode === 'dark'
                          ? '1px solid rgba(255,255,255,0.2)'
                          : '1px solid rgba(0,0,0,0.12)'
                      },
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    {/* Premium gradient overlay */}
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 6,
                      background: card.is_published 
                        ? 'linear-gradient(135deg, #4caf50 0%, #2e7d32 50%, #1b5e20 100%)'
                        : 'linear-gradient(135deg, #ff9800 0%, #f57c00 50%, #e65100 100%)',
                      boxShadow: card.is_published 
                        ? '0 2px 8px rgba(76, 175, 80, 0.3)'
                        : '0 2px 8px rgba(255, 152, 0, 0.3)'
                    }} />
                    
                    {/* Card Header with enhanced design */}
                    <Box sx={{ p: 3, pb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar 
                          sx={{ 
                            background: card.is_published 
                              ? 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)'
                              : 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                            width: 56,
                            height: 56,
                            boxShadow: card.is_published 
                              ? '0 4px 12px rgba(76, 175, 80, 0.3)'
                              : '0 4px 12px rgba(255, 152, 0, 0.3)'
                          }}
                        >
                          <BusinessCenter sx={{ fontSize: 24 }} />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ 
                            fontWeight: 700, 
                            fontSize: '1.2rem',
                            color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
                            mb: 0.5
                          }}>
                            {card.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ 
                            fontSize: '0.9rem',
                            fontWeight: 500
                          }}>
                            {card.designation || 'Professional'}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Company info with enhanced styling */}
                      {card.companyName && (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1.5,
                          mb: 2,
                          p: 1.5,
                          borderRadius: 2,
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.05)'
                            : 'rgba(0,0,0,0.03)',
                          border: theme.palette.mode === 'dark'
                            ? '1px solid rgba(255,255,255,0.1)'
                            : '1px solid rgba(0,0,0,0.06)'
                        }}>
                          <BusinessCenter sx={{ 
                            fontSize: 18, 
                            color: 'primary.main',
                            opacity: 0.8
                          }} />
                          <Typography variant="body2" color="text.secondary" sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem'
                          }}>
                            {card.companyName}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Contact info preview with enhanced icons */}
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        mb: 2,
                        flexWrap: 'wrap'
                      }}>
                        {card.phone && (
                          <Tooltip title="Phone available">
                            <Box sx={{
                              p: 1,
                              borderRadius: 2,
                              background: 'rgba(76, 175, 80, 0.1)',
                              border: '1px solid rgba(76, 175, 80, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}>
                              <Phone sx={{ fontSize: 16, color: 'success.main' }} />
                              <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                Phone
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}
                        {card.email && (
                          <Tooltip title="Email available">
                            <Box sx={{
                              p: 1,
                              borderRadius: 2,
                              background: 'rgba(33, 150, 243, 0.1)',
                              border: '1px solid rgba(33, 150, 243, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}>
                              <Email sx={{ fontSize: 16, color: 'primary.main' }} />
                              <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                                Email
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}
                        {card.website && (
                          <Tooltip title="Website available">
                            <Box sx={{
                              p: 1,
                              borderRadius: 2,
                              background: 'rgba(156, 39, 176, 0.1)',
                              border: '1px solid rgba(156, 39, 176, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}>
                              <Language sx={{ fontSize: 16, color: 'secondary.main' }} />
                              <Typography variant="caption" color="secondary.main" sx={{ fontWeight: 600 }}>
                                Website
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                      
                      {/* Status chips with enhanced design */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mb: 2
                      }}>
                        <Chip 
                          label={card.is_published ? 'Live' : 'Draft'} 
                          color={card.is_published ? 'success' : 'warning'} 
                          size="small" 
                          icon={card.is_published ? <Visibility /> : <VisibilityOff />}
                          sx={{ 
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            height: 24,
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                        <Chip 
                          icon={<QrCodeScanner />}
                          label="QR Ready"
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ 
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: 24,
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      </Box>
                    </Box>
                    
                    {/* Enhanced action buttons */}
                    <CardActions sx={{ 
                      p: 2, 
                      pt: 1,
                      justifyContent: 'space-between',
                    }}>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View Analytics">
                          <IconButton 
                            component={Link} 
                            to="/analytics"
                            sx={{ 
                              width: 48, height: 48, minWidth: 0, padding: 0, boxSizing: 'border-box',
                              backgroundColor: 'primary.main',
                              color: 'white',
                              '&:hover': { 
                                backgroundColor: 'primary.dark',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <Analytics sx={{ fontSize: 24 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Share Card">
                          <IconButton 
                            onClick={() => handleShareClick(card)}
                            sx={{ 
                              width: 48, height: 48, minWidth: 0, padding: 0, boxSizing: 'border-box',
                              backgroundColor: 'success.main',
                              color: 'white',
                              '&:hover': { 
                                backgroundColor: 'success.dark',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <QrCodeScanner sx={{ fontSize: 24 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit Card">
                          <IconButton 
                            component={Link} 
                            to={`/edit-card/${card.slug}`}
                            sx={{ 
                              width: 48, height: 48, minWidth: 0, padding: 0, boxSizing: 'border-box',
                              backgroundColor: 'info.main',
                              color: 'white',
                              '&:hover': { 
                                backgroundColor: 'info.dark',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <Edit sx={{ fontSize: 24 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Card">
                          <IconButton 
                            onClick={() => onDelete(card.id)}
                            sx={{ 
                              width: 48, height: 48, minWidth: 0, padding: 0, boxSizing: 'border-box',
                              backgroundColor: 'error.main',
                              color: 'white',
                              '&:hover': { 
                                backgroundColor: 'error.dark',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <Delete sx={{ fontSize: 24 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </CardActions>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      )}
      
      {selectedCard && (
        <CardShare
          open={shareDialogOpen}
          onClose={handleCloseShareDialog}
          card={selectedCard}
        />
      )}
    </Box>
  );
};

export default CardList; 